import React, { ChangeEvent, Component } from 'react';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import { ShortDateFormat } from '@utils/library/dateTime';
import cn from 'classnames';
import { connectToContext, IAppContext } from 'contextStore';
import moment from 'moment';
import { Attributes, FormType, GetAttributesType } from './formField.interface';

interface IDropdownDatePickerProps extends IAppContext {
  name: string;
  value: string;
  valid: boolean;
  attributes: Attributes;
  getAttributes: GetAttributesType;
  form: FormType;
  onChange: (name: string, value?: string) => void;
  useMonthNames: boolean;
  padMonth: boolean;
  padDay: boolean;
  maxYear: number;
  minYear: number;
  title: string;
  validationMessage: string;
  dayLabel: string;
  monthLabel: string;
  yearLabel: string;
  required: boolean;
  hintMessage?: string;
  className: string;
  testID?: string;
  tooltip?: string;
  placeholders?: Record<string, string>;
}

type SelectValueType = string | number;

interface IDropdownDatePickerState {
  [key: string]: SelectValueType | boolean;
  selectDay: SelectValueType;
  selectMonth: SelectValueType;
  selectYear: SelectValueType;
  isValid: boolean;
}

type DateObjectType = {
  year: number;
  month: number;
  day: number;
};

type DropdownDatePickerOption = {
  value: number;
  title?: SelectValueType;
  name?: string;
};

const defaultProps = {
  dayLabel: keyMap.formsDatePlaceholdersDay,
  monthLabel: keyMap.formsDatePlaceholdersMonth,
  padDay: false,
  padMonth: false,
  selectDay: '',
  selectMonth: '',
  selectYear: '',
  yearLabel: keyMap.formsDatePlaceholdersYear,
  useMonthNames: true,
  dateFormat: ShortDateFormat,
  hintMessage: null,
};

class DropdownDatePicker extends Component<IDropdownDatePickerProps, IDropdownDatePickerState> {
  state: IDropdownDatePickerState = {
    selectDay: '',
    selectMonth: '',
    selectYear: '',
    isValid: true,
  };

  componentDidMount(): void {
    const { value } = this.props;

    if (value) {
      this.setValue(value);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: IDropdownDatePickerProps): void {
    const { value } = this.props;

    if (nextProps.value && nextProps.value !== value) {
      this.setValue(nextProps.value);
    }
  }

  setValue(value: string): void {
    this.setState({
      selectDay: value ? moment(value).date() : '',
      selectMonth: value ? moment(value).month() + 1 : '',
      selectYear: value ? moment(value).year() : '',
    });
  }

  changeDate = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;

    this.setState({
      [name]: value,
    });
    this.checkDate(value, name);
  };

  getDate = (date: DateObjectType): string | undefined =>
    moment(date).isValid() ? moment(date).startOf('day').format(defaultProps.dateFormat) : undefined;

  checkDate(value: string, type: string): void {
    let { selectDay, selectMonth, selectYear } = this.state;
    const { onChange, name } = this.props;

    if (type === 'selectDay') {
      selectDay = value;
    } else if (type === 'selectMonth') {
      selectMonth = value;
    } else if (type === 'selectYear') {
      selectYear = value;
    }

    if (this.isSelectedAllDropdowns(selectDay, selectMonth, selectYear)) {
      const dateObject: DateObjectType = {
        year: Number(selectYear),
        month: Number(selectMonth) - 1,
        day: Number(selectDay),
      };

      const date = this.getDate(dateObject);

      this.setState({ isValid: !!date }, () => onChange(name, date));
    } else {
      onChange(name, undefined);
    }
  }

  isSelectedAllDropdowns = (
    selectDay: SelectValueType,
    selectMonth: SelectValueType,
    selectYear: SelectValueType,
  ): unknown => selectDay && selectMonth && selectYear;

  pad = (n: number): string | number => (n < 10 ? `0${n}` : n);

  getDays = (): { value: number; title: string | number }[] => {
    const { padDay = defaultProps.padDay } = this.props;
    const days = [];

    for (let i = 1; i <= 31; i += 1) {
      days.push({ value: i, title: padDay ? this.pad(i) : i });
    }

    return days;
  };

  getMonths = (): { title: string | number; value: number }[] => {
    const { useMonthNames = defaultProps.useMonthNames, padMonth = defaultProps.padMonth, i18next } = this.props;

    const localizedMonths = moment.localeData(i18next.language || '').months();

    return localizedMonths.map((month, index) => {
      const monthName = padMonth ? this.pad(index + 1) : index + 1;

      return {
        title: useMonthNames ? month : monthName,
        value: index + 1,
      };
    });
  };

  getYears = (): { value: number }[] => {
    const { maxYear, minYear } = this.props;

    const years = [];

    for (let i = maxYear; i >= minYear; i -= 1) {
      years.push({ value: i });
    }

    return years;
  };

  renderElement(name: string, value: SelectValueType, options: DropdownDatePickerOption[], label: string): JSX.Element {
    const { valid, attributes, getAttributes, form, testID = 'formDropdownDatePicker', i18next } = this.props;
    const { isValid } = this.state;
    const attr = getAttributes ? { ...attributes, ...getAttributes(form) } : attributes;
    const className = cn('form__input', { form__input_error: !valid }, { invalid: !valid || !isValid });
    const optionTitle = (opt: DropdownDatePickerOption) => opt.title || opt.name || opt.value;

    return (
      <div className="form__field-group-item form__field--select form__select-wrapper" data-test-id={testID}>
        <select className={className} name={name} value={value} onChange={this.changeDate} {...attr}>
          <option value="">{i18next.t(label as KeyMapKeys)}</option>
          {options.map((o) => (
            <option value={o.value} key={o.value}>
              {i18next.t(String(optionTitle(o)) as KeyMapKeys)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  render(): JSX.Element {
    const {
      name,
      title,
      valid,
      validationMessage,
      dayLabel = defaultProps.dayLabel,
      monthLabel = defaultProps.monthLabel,
      yearLabel = defaultProps.yearLabel,
      required,
      hintMessage,
      className,
      placeholders,
      tooltip,
      i18next,
    } = this.props;

    const { selectDay, selectMonth, selectYear, isValid } = this.state;

    const validMessage = !isValid
      ? keyMap.formsValidationInvalidDate
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        i18next.t(validationMessage as KeyMapKeys, placeholders as any);

    return (
      <InputFieldWrapper
        name={name}
        title={title}
        className={className}
        tooltip={tooltip}
        required={required}
        hintMessage={hintMessage}
        validationMessage={validMessage as string}
        valid={valid && isValid}>
        <div className="form__field-group">
          {this.renderElement('selectDay', selectDay, this.getDays(), dayLabel)}
          {this.renderElement('selectMonth', selectMonth, this.getMonths(), monthLabel)}
          {this.renderElement('selectYear', selectYear, this.getYears(), yearLabel)}
        </div>
      </InputFieldWrapper>
    );
  }
}

export default connectToContext(DropdownDatePicker);
